import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  Lock,
  Mail,
  Shield,
  Users,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/auth';
import toast from 'react-hot-toast';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

const DEFAULT_FORCE_MESSAGE = 'Akun ini masih login di perangkat lain. Lanjutkan masuk di perangkat ini? Sesi sebelumnya akan diakhiri.';

const normalizeValidationErrors = (validationErrors: Record<string, string | string[]>) => {
  return Object.fromEntries(
    Object.entries(validationErrors).map(([field, value]) => [
      field,
      Array.isArray(value) ? value[0] || '' : value,
    ])
  ) as Record<string, string>;
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forceLoading, setForceLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoError, setLogoError] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [bgError, setBgError] = useState(false);
  const [showForceModal, setShowForceModal] = useState(false);
  const [forceModalMessage, setForceModalMessage] = useState(DEFAULT_FORCE_MESSAGE);

  const { setUser, setToken, setCapabilities } = useAuthStore();
  const { settings, fetchPublicSettings } = useSystemSettingsStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  useEffect(() => {
    setBgError(false);
  }, [settings.login_background]);

  const appName = settings.app_name || 'SIM-EVAL';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const institutionAddress = settings.institution_address || '';
  const institutionPhone = settings.institution_phone || '';
  const institutionEmail = settings.institution_email || '';
  const institutionWebsite = settings.institution_website || '';
  const heroDescription = settings.app_description?.trim()
    ? `${settings.app_description}. Mendukung pelaporan, validasi berjenjang, tindak lanjut Tindak Lanjut, dan pemantauan penyelesaian mutu pendidikan.`
    : 'Platform terpadu untuk pelaporan, validasi berjenjang, tindak lanjut Tindak Lanjut, dan pemantauan penyelesaian mutu pendidikan.';
  const readableFooterText = settings.footer_text || 'Copyright 2026 SIM-EVAL UBP Karawang. Hak cipta dilindungi.';

  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part: string) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const loginBgUrl = getAssetUrl(settings.login_background);
  const showLoginBg = loginBgUrl && !bgError;

  useEffect(() => {
    if (appName) {
      document.title = appName;
    }

    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }
  }, [appName, settings.favicon_url]);

  const handleLoginError = (err: any) => {
    if (err.response?.status === 422) {
      const validationErrors = normalizeValidationErrors(err.response.data?.errors || {});
      setErrors(validationErrors);
      toast.error(err.response.data?.message || 'Periksa data yang Anda masukkan');
    } else if (err.response?.status === 401) {
      setErrors({ email: 'Email atau password tidak cocok' });
      toast.error('Email atau password tidak cocok');
    } else if (err.code === 'ERR_NETWORK' || !err.response) {
      toast.error('Tidak dapat terhubung ke server');
    } else {
      toast.error(err.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const completeLogin = (user: Parameters<typeof setUser>[0], token: string, capabilities: string[]) => {
    setToken(token);
    setUser(user);
    setCapabilities(capabilities || []);
    toast.success(`Selamat datang, ${user.name}!`);
    navigate('/dashboard');
  };

  const submitLogin = async (force: boolean) => {
    if (force) {
      setForceLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await authService.login(email, password, force);

      if (result.alreadyLoggedIn) {
        setForceModalMessage(result.message || DEFAULT_FORCE_MESSAGE);
        setShowForceModal(true);
        return;
      }

      setShowForceModal(false);
      setForceModalMessage(DEFAULT_FORCE_MESSAGE);
      completeLogin(result.user, result.token, result.capabilities);
    } catch (err: any) {
      handleLoginError(err);
    } finally {
      if (force) {
        setForceLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setShowForceModal(false);

    if (!email) {
      setErrors((prev) => ({ ...prev, email: 'Email wajib diisi' }));
      return;
    }

    if (!password) {
      setErrors((prev) => ({ ...prev, password: 'Password wajib diisi' }));
      return;
    }

    await submitLogin(false);
  };

  const handleForceLogin = async () => {
    setShowForceModal(false);
    await submitLogin(true);
  };

  const features = [
    { icon: <FileText className="w-5 h-5" />, title: 'Pelaporan Masalah', desc: 'Catat temuan dan pantau status penanganannya.' },
    { icon: <Shield className="w-5 h-5" />, title: 'Validasi Berjenjang', desc: 'Persetujuan berjenjang sesuai kewenangan pimpinan.' },
    { icon: <Users className="w-5 h-5" />, title: 'Rapat Pimpinan', desc: 'Kelola agenda, keputusan, dan tindak lanjut RAPIM.' },
    { icon: <BarChart3 className="w-5 h-5" />, title: 'Pemantauan Langsung', desc: 'Ikuti progres penyelesaian secara lebih jelas.' },
  ];

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {showLoginBg ? (
          <div
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ backgroundImage: `url(${loginBgUrl})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628]/90 via-[#0f33ab]/60 to-[#1a4fd6]/40" />
            <img
              src={loginBgUrl}
              alt=""
              className="hidden"
              onError={() => setBgError(true)}
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f33ab] to-[#1a4fd6]" />
        )}

        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-blue-400/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-white/[0.03]" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <div className="flex items-center gap-4 mb-16">
              <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded-2xl shrink-0 bg-white/10 backdrop-blur-sm">
                {showLogo ? (
                  <img
                    src={logoUrl!}
                    alt="Logo"
                    className="w-10 h-10 object-contain"
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <span className="text-2xl font-black text-white">{brandInitials}</span>
                )}
              </div>
              <div>
                <h1 className="text-xl font-black text-white tracking-tight">{appName}</h1>
                <p className="text-sm text-blue-200/70">{institutionName}</p>
              </div>
            </div>

            <div className="max-w-lg">
              <h2 className="text-4xl font-black text-white leading-tight mb-4">
                Kelola Evaluasi Mutu
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">
                  Secara Terpadu
                </span>
              </h2>
              <p className="text-base text-blue-100/60 leading-relaxed">
                {heroDescription}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 my-8">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group p-4 rounded-2xl bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] hover:bg-white/[0.1] hover:border-white/[0.15] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-blue-200 mb-3 group-hover:bg-white/15 transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{feature.title}</h3>
                <p className="text-xs text-blue-200/50 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            {institutionAddress && (
              <p className="text-xs text-blue-200/40">{institutionAddress}</p>
            )}
            <div className="flex items-center gap-4 text-xs text-blue-200/30">
              {institutionPhone && <span>{institutionPhone}</span>}
              {institutionEmail && <span>{institutionEmail}</span>}
            </div>
            {institutionWebsite && (
              <p className="text-xs text-blue-200/30">{institutionWebsite}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#f8f9fb] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0f33ab]/[0.02] rounded-full -mr-48 -mt-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0f33ab]/[0.03] rounded-full -ml-32 -mb-32" />

        <div className="relative z-10 w-full max-w-[420px] px-6 sm:px-8">
          <div className="lg:hidden text-center mb-10">
            <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 overflow-hidden rounded-2xl ${showLogo ? 'bg-transparent' : 'bg-[#0f33ab] shadow-lg shadow-[#0f33ab]/30'}`}>
              {showLogo ? (
                <img
                  src={logoUrl!}
                  alt="Logo"
                  className="w-full h-full object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-2xl font-black text-white">{brandInitials}</span>
              )}
            </div>
            <h1 className="text-2xl font-black text-[#0f33ab] tracking-tight">{appName}</h1>
            <p className="text-sm text-gray-500 mt-1">{institutionName}</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-black text-gray-900 tracking-tight">
              Selamat Datang
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              Masukkan email institusi dan password untuk melanjutkan ke sistem.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Email
              </label>
              <div className={`relative rounded-xl transition-all duration-200 ${focusedField === 'email' ? 'ring-2 ring-[#0f33ab]/20' : ''}`}>
                <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl transition-colors ${focusedField === 'email' ? 'bg-[#0f33ab] text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="nama@ubpkarawang.ac.id"
                  className={`w-full pl-14 pr-4 py-3.5 rounded-xl border-2 text-sm font-medium bg-white focus:outline-none transition-all ${errors.email ? 'border-red-300 focus:border-red-400' : focusedField === 'email' ? 'border-[#0f33ab]' : 'border-gray-200 hover:border-gray-300'}`}
                />
              </div>
              {errors.email && (
                <p className="text-xs text-red-500 font-medium flex items-center gap-1 mt-1">
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Password
              </label>
              <div className={`relative rounded-xl transition-all duration-200 ${focusedField === 'password' ? 'ring-2 ring-[#0f33ab]/20' : ''}`}>
                <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl transition-colors ${focusedField === 'password' ? 'bg-[#0f33ab] text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Masukkan password"
                  className={`w-full pl-14 pr-12 py-3.5 rounded-xl border-2 text-sm font-medium bg-white focus:outline-none transition-all ${errors.password ? 'border-red-300 focus:border-red-400' : focusedField === 'password' ? 'border-[#0f33ab]' : 'border-gray-200 hover:border-gray-300'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-[#0f33ab] hover:bg-[#0f33ab]/5 transition-all"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 font-medium flex items-center gap-1 mt-1">
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  {errors.password}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-2 border-gray-300 text-[#0f33ab] focus:ring-[#0f33ab]/20 cursor-pointer"
                />
                <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors font-medium">
                  Ingat saya
                </span>
              </label>
              <a
                href="/forgot-password"
                className="text-sm font-semibold text-[#0f33ab] hover:text-[#0a2478] transition-colors"
              >
                Lupa password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading || forceLoading}
              className={`relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group transition-all duration-300 ${loading || forceLoading ? 'bg-[#0f33ab]/70 cursor-not-allowed' : 'bg-[#0f33ab] hover:shadow-lg hover:shadow-[#0f33ab]/30 active:scale-[0.98]'}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center justify-center gap-2">
                {loading || forceLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Memproses...
                  </>
                ) : (
                  <>
                    Masuk ke Sistem
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </span>
            </button>
          </form>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Akses dilindungi koneksi terenkripsi</span>
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            {readableFooterText}
          </p>
        </div>
      </div>

      {showForceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowForceModal(false)}
          />

          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#0f33ab] to-[#1a4fd6] px-6 py-5 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Sesi Aktif Ditemukan</h3>
                  <p className="text-xs text-blue-200/80">Peringatan keamanan sistem</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowForceModal(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors text-white/70 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                {forceModalMessage}
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Jika Anda melanjutkan, <span className="font-semibold text-red-600">semua sesi lain untuk akun {email} akan dihentikan</span> dan Anda akan masuk di perangkat ini.
              </p>

              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Jika ini bukan aktivitas Anda, segera ganti kata sandi atau hubungi administrator untuk pemeriksaan lebih lanjut.
                </p>
              </div>
            </div>

            <div className="px-6 pb-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowForceModal(false)}
                className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleForceLogin}
                disabled={forceLoading}
                className="flex-1 py-3 rounded-xl bg-[#0f33ab] text-sm font-bold text-white hover:bg-[#0a2478] hover:shadow-lg hover:shadow-[#0f33ab]/30 active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {forceLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Memproses...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4" />
                    Lanjutkan Login
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
